import { useEffect, useState } from "react";
import type { Title } from "@/types/media";

interface PlayerDemoProps {
  item: Title;
}

export default function PlayerDemo({ item }: PlayerDemoProps) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!playing) {
      setProgress(0);
      return;
    }
    const timer = window.setInterval(() => {
      setProgress((prev) => (prev >= 100 ? 0 : prev + 1));
    }, 120);
    return () => window.clearInterval(timer);
  }, [playing]);

  return (
    <div className="rounded-lg overflow-hidden bg-background-900 border border-background-800">
      <div className="relative w-full aspect-video bg-background-950">
        <img
          src={item.banner}
          alt={`${item.title} 播放画面`}
          title={`${item.title} 高清在线观看`}
          className={`absolute inset-0 w-full h-full object-cover object-top transition-all duration-500 ${
            playing ? "opacity-30 blur-sm" : "opacity-70"
          }`}
        />
        {!playing && (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 cursor-pointer group"
            aria-label={`演示播放 ${item.title}`}
          >
            <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500 group-hover:bg-primary-600 flex items-center justify-center transition-colors">
              <i className="ri-play-fill text-3xl md:text-4xl text-background-50"></i>
            </span>
            <span className="px-3 py-1 rounded-full bg-background-950/70 border border-background-700 text-xs text-foreground-200">
              演示播放器 · 点击体验
            </span>
          </button>
        )}

        {playing && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <i className="ri-loader-4-line text-4xl text-primary-400 animate-spin mb-4"></i>
            <p className="text-foreground-100 font-medium">正在播放演示画面</p>
            <p className="text-xs text-foreground-400 mt-2">
              演示模式 · 本页面未接入真实视频源，仅用于展示播放器界面
            </p>
          </div>
        )}
      </div>

      <div className="px-4 md:px-5 py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setPlaying((v) => !v)}
            className="w-9 h-9 rounded-full bg-primary-500 hover:bg-primary-600 flex items-center justify-center text-background-50 cursor-pointer transition-colors shrink-0"
            aria-label={playing ? "暂停演示" : "开始演示"}
          >
            <i className={playing ? "ri-pause-fill" : "ri-play-fill"}></i>
          </button>
          <div className="flex-1 h-1.5 rounded-full bg-background-800 overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full transition-[width] duration-100"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <span className="text-xs text-foreground-400 tabular-nums shrink-0 w-20 text-right">
            {Math.floor((progress / 100) * 118)}:{String(Math.floor(progress * 0.6) % 60).padStart(2, "0")} / {item.duration}
          </span>
        </div>
        <p className="text-xs text-foreground-500 mt-3 flex items-center gap-1">
          <i className="ri-information-line"></i>
          当前为前端演示播放区域，不加载真实视频内容。
        </p>
      </div>
    </div>
  );
}